import type { CSSProperties, ReactNode } from "react";
import { Checkbox } from "../Checkbox";
import { cx } from "../cx";
import { Icon } from "../Icon";
import styles from "./Table.module.css";

export type SortDirection = "asc" | "desc";

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  align?: "left" | "right" | "center";
  /** CSS width (px number or any length). */
  width?: number | string;
  sortable?: boolean;
  /** Footer cell content (totals row). */
  footer?: ReactNode;
  className?: string;
}

export interface TableSort {
  key: string;
  direction: SortDirection;
}

export interface TableProps<T> {
  columns: ReadonlyArray<TableColumn<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string;
  /** 40px rows (lists, settings) instead of the 60px tracker-row default. */
  dense?: boolean;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  onRowClick?: (row: T) => void;
  /** Shown in place of the body when `rows` is empty. */
  empty?: ReactNode;
  /** Renders a totals row from each column's `footer`. */
  showFooter?: boolean;
  stickyHeader?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  dense,
  selectable,
  selected,
  onSelectedChange,
  sort,
  onSortChange,
  onRowClick,
  empty,
  showFooter,
  stickyHeader = true,
  className,
  ...rest
}: TableProps<T>) {
  const sel = selected ?? new Set<string>();
  const keys = rows.map(rowKey);
  const selectedCount = keys.filter((k) => sel.has(k)).length;
  const allSelected = keys.length > 0 && selectedCount === keys.length;

  const toggleAll = () => {
    const next = new Set(sel);
    if (allSelected) for (const k of keys) next.delete(k);
    else for (const k of keys) next.add(k);
    onSelectedChange?.(next);
  };
  const toggleOne = (k: string) => {
    const next = new Set(sel);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onSelectedChange?.(next);
  };

  const cellStyle = (c: TableColumn<T>): CSSProperties | undefined =>
    c.width === undefined ? undefined : { width: c.width };
  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className={cx(styles.wrap, className)}>
      <table className={cx(styles.table, dense && styles.dense)} aria-label={rest["aria-label"]}>
        <thead className={cx(stickyHeader && styles.sticky)}>
          <tr>
            {selectable && (
              <th className={styles.selectCell}>
                <Checkbox
                  aria-label="Select all"
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const ariaSort = active ? (sort?.direction === "asc" ? "ascending" : "descending") : undefined;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={cx(styles[c.align ?? "left"], c.className)}
                  style={cellStyle(c)}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={() =>
                        onSortChange({
                          key: c.key,
                          direction: active && sort?.direction === "asc" ? "desc" : "asc",
                        })
                      }
                    >
                      {c.header}
                      <Icon
                        name={active && sort?.direction === "desc" ? "chevron-down" : "chevron-up"}
                        size={12}
                        strokeWidth={2}
                        className={cx(styles.sortIcon, active && styles.sortActive)}
                      />
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className={styles.emptyCell}>
                {empty ?? "No data"}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const k = keys[i] as string;
              const isSel = sel.has(k);
              return (
                <tr
                  key={k}
                  className={cx(styles.row, onRowClick && styles.clickable, isSel && styles.selected)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    // Selecting must not also trigger the row's click action.
                    // biome-ignore lint/a11y/useKeyWithClickEvents: the checkbox inside handles keyboard input
                    <td className={styles.selectCell} onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label="Select row" checked={isSel} onChange={() => toggleOne(k)} />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className={cx(styles[c.align ?? "left"], c.className)} style={cellStyle(c)}>
                      {c.render(row, i)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
        {showFooter && rows.length > 0 && (
          <tfoot>
            <tr className={styles.footerRow}>
              {selectable && <td className={styles.selectCell} />}
              {columns.map((c) => (
                <td key={c.key} className={cx(styles[c.align ?? "left"], c.className)}>
                  {c.footer}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
